/**
 * The caution banner ABOVE the content it qualifies.
 *
 * A hazard is a RESULT, not a bound: an unapplied amendment to a
 * statute and a retraction notice on a paper both change what the record
 * below them means, and a reader who meets them after reading it has already
 * been misinformed.
 *
 * It sweeps once on first reveal and never again. A banner that re-animates
 * every time its list re-renders becomes wallpaper, and wallpaper is the one
 * thing a hazard cannot be.
 *
 * The banner says three different things and never the same thing twice. The
 * `summary` is what is WRONG, stated by the caller in one line, and it is the
 * headline beside the count. The `cause` is what produced it, and it is the
 * collapsed group's own summary — the fact that makes collapsing the list
 * acceptable. The `label` names what is INSIDE the group in the pillar's own
 * vocabulary, because a retraction notice and an unapplied amendment are not
 * both "Effects".
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import { IconAlertTriangle } from "@tabler/icons-react";
import {
  CRC_R_CORE,
  CRC_TONE_COLOR,
} from "./cresultcard.tokens";
import { CresultcardTone } from "./cresultcard.types";
import { CresultcardDisclosure } from "./CresultcardDisclosure";

const GLYPH_SIZE = 14;
const GLYPH_STROKE = 1.6;

interface CresultcardHazardProps {
  /** How many. The count is the reason the banner exists. */
  readonly count: number;
  /** What is wrong with the content below, in one line. Never the cause. */
  readonly summary: string;
  /** What caused it, in the reader's terms. The supporting detail. */
  readonly cause: string;
  /** What the collapsed group holds, in the pillar's own vocabulary. */
  readonly label: string;
  readonly children: ReactNode;
}

export function CresultcardHazard({
  count,
  summary,
  cause,
  label,
  children,
}: CresultcardHazardProps): ReactElement {
  const color = CRC_TONE_COLOR[CresultcardTone.Caution];

  return (
    <div
      data-el="Cresultcard/Hazard/Root"
      role="note"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minWidth: 0,
        paddingBlock: 9,
        paddingInline: 11,
        borderRadius: CRC_R_CORE,
        backgroundColor: "rgba(242,159,87,0.08)",
        boxShadow: "inset 0 0 0 1px rgba(242,159,87,0.28)",
      }}
    >
      <span
        className="cresultcard__hazard-rule"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          minWidth: 0,
        }}
      >
        <IconAlertTriangle
          color={color}
          size={GLYPH_SIZE}
          stroke={GLYPH_STROKE}
        />
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 740,
            fontVariantNumeric: "tabular-nums",
            color,
          }}
        >
          {count}
        </span>
        <span
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            fontSize: 10.5,
            fontWeight: 560,
            lineHeight: 1.3,
            color,
          }}
        >
          {summary}
        </span>
      </span>
      <CresultcardDisclosure
        label={label}
        summary={cause}
      >
        {children}
      </CresultcardDisclosure>
    </div>
  );
}
